import React, { useRef, useState } from 'react';
import { SERIES_COLORS, fmtNum, niceScale } from './chartTheme';

// Vertical bars, optionally stacked.
//
//   data:   [{ label, values: [n, ...] }]   values align with `series`
//   series: [{ name, color? }]              omit for a single unnamed series
export default function BarChart({
  data = [],
  series = [{ name: 'value' }],
  height = 200,
  formatY = fmtNum,
  formatLabel = (l) => String(l),
}) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { i, leftPct }

  const W = 640;
  const H = height;
  const PAD = { l: 42, r: 12, t: 12, b: 22 };
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;

  if (!data.length) return <div className="ck-empty">No data</div>;

  const totals = data.map((d) => d.values.reduce((a, b) => a + (b || 0), 0));
  const { max: yMax, ticks: yTicks } = niceScale(Math.max(...totals, 1));
  const py = (y) => PAD.t + (1 - y / yMax) * ih;

  const slot = iw / data.length;
  const barW = Math.min(34, slot * 0.62);

  const labelStep = Math.max(1, Math.round(data.length / 6));

  const onMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.max(0, Math.min(data.length - 1, Math.floor((relX - PAD.l) / slot)));
    setHover({ i, leftPct: ((PAD.l + slot * i + slot / 2) / W) * 100 });
  };

  return (
    <div className="ck-wrap" ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="ck-svg" preserveAspectRatio="none" aria-hidden="true">
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={py(v)} y2={py(v)} className="ck-grid" />
            <text x={PAD.l - 6} y={py(v) + 3} className="ck-tick" textAnchor="end">{formatY(v)}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = PAD.l + slot * i + (slot - barW) / 2;
          let acc = 0;
          return (
            <g key={d.label} className={hover && hover.i === i ? 'ck-bar-hot' : ''}>
              {d.values.map((v, si) => {
                const y0 = acc;
                acc += v || 0;
                return v > 0 ? (
                  <rect
                    key={si}
                    x={x}
                    y={py(acc)}
                    width={barW}
                    height={py(y0) - py(acc)}
                    rx="2"
                    fill={series[si]?.color || SERIES_COLORS[si]}
                    className="ck-bar"
                  />
                ) : null;
              })}
            </g>
          );
        })}
        {data.map((d, i) => (i % labelStep === 0 ? (
          <text key={d.label} x={PAD.l + slot * i + slot / 2} y={H - 6} className="ck-tick" textAnchor="middle">
            {formatLabel(d.label)}
          </text>
        ) : null))}
      </svg>
      {series.length > 1 && (
        <div className="ck-legend">
          {series.map((s, si) => (
            <span key={s.name}><i style={{ background: s.color || SERIES_COLORS[si] }} />{s.name}</span>
          ))}
        </div>
      )}
      {hover && (
        <div className="ck-tip" style={{ left: `${hover.leftPct}%` }}>
          <div className="ck-tip-title">{formatLabel(data[hover.i].label)}</div>
          {series.map((s, si) => (
            <div key={s.name} className="ck-tip-row">
              <i style={{ background: s.color || SERIES_COLORS[si] }} />
              {s.name}: <strong>{formatY(data[hover.i].values[si] || 0)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
